import { Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Context from '../Context'
import { useClock } from '../utils/clock'
import theme from '../theme'
import { useNavigate } from 'react-router-dom'
import { apiJson, apiFetch } from '../utils/api'
import { playAlert } from '../utils/sound'

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

const DELAY_SECONDS = 5

const SAMPLE = {
  title: 'Arb 2.14% · Philadelphia Phillies vs Washington Nationals',
  body: 'Moneyline · Philadelphia Phillies\nBovada 1.72   Pinnacle 2.43'
}

const supported = () => typeof window !== 'undefined' && 'Notification' in window


const SCOPE_ORDER = { sport: 0, league: 1, event: 2 }
const SCOPE_LABEL = { sport: 'Sport', league: 'League', event: 'Event' }

function NotificationSettingsPage() {
  const { marketHost, settings, setSettings } = useContext(Context)
  const clock = useClock()
  const navigate = useNavigate()

  const [ permission, setPermission ] = useState(supported() ? Notification.permission : 'unsupported')
  const [ mutes, setMutes ] = useState([])
  const [ secondsLeft, setSecondsLeft ] = useState(null)
  const [ lines, setLines ] = useState([])
  const timer = useRef(null)
  const tick = useRef(null)
  const counter = useRef(1)

  useEffect(() => () => {
    clearTimeout(timer.current)
    clearInterval(tick.current)
  }, [])

  /**
   * Chrome does not tell a page when the padlock menu changes, so the state read at mount goes stale and
   * the paragraph keeps saying "blocked" while notifications are in fact arriving. Re-reading whenever
   * the tab is looked at again covers the way this is actually changed: leave, change it, come back.
   */
  useEffect(() => {
    if (!supported()) {
      return undefined
    }
    const refresh = () => setPermission(Notification.permission)
    refresh()
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [])

  const say = (text) => setLines((prev) => [`${clock.seconds(Date.now())}  ${text}`, ...prev].slice(0, 6))

  const loadMutes = useCallback(() => {
    apiJson(marketHost, '/api/mutes')
      .then((data) => setMutes(data.mutes || []))
      .catch(() => {})
  }, [marketHost])

  useEffect(() => { loadMutes() }, [loadMutes])

  const unmute = async (id) => {
    setMutes((prev) => prev.filter((row) => row.id !== id))
    await apiFetch(marketHost, `/api/mutes?id=${id}`, { method: 'DELETE' }).catch(() => {})
  }

  // no Save button here: every control on this page is one decision, and holding it hostage to a button
  // is how a muted league quietly stays noisy
  const toggleSound = () => setSettings({ ...settings, notificationSound: settings.notificationSound === false })
  const toggleDesktop = () => setSettings({ ...settings, desktopNotifications: settings.desktopNotifications === false })

  // typed into freely and only committed when the field is left, so a half typed id is never saved
  const [ chatId, setChatId ] = useState(settings.telegramChatId || '')
  useEffect(() => { setChatId(settings.telegramChatId || '') }, [settings.telegramChatId])
  const saveChatId = () => {
    const next = chatId.trim()
    if (next !== (settings.telegramChatId || '')) {
      setSettings({ ...settings, telegramChatId: next })
    }
  }

  const ensurePermission = async () => {
    if (!supported()) {
      say('This browser has no Notification API.')
      return false
    }
    // the switch above is part of the chain being tested, not something the test gets to step around
    if (settings.desktopNotifications === false) {
      say('Desktop notifications are switched off for this account — turn the switch above on first.')
      return false
    }
    let granted = Notification.permission
    if (granted === 'default') {
      say('Asking Chrome for permission…')
      granted = await Notification.requestPermission()
      setPermission(granted)
    }
    if (granted !== 'granted') {
      say(`Permission is "${granted}". Allow it in the padlock menu next to the address bar.`)
      return false
    }
    return true
  }

  /**
   * onshow firing while the screen stays empty is the useful signal: Chrome did its part and the
   * notification is being held back by the operating system, which is a different place to go and fix.
   */
  const fire = () => {
    playAlert(0.5)

    let reported = false
    let notification
    try {
      // a repeated tag replaces what is already there, and a replacement draws no banner
      notification = new Notification(SAMPLE.title, {
        body: SAMPLE.body,
        icon: '/logo192.png',
          // unique for the life of the page, not just the life of the component: the counter restarts
        // whenever this screen is remounted, and a repeated tag replaces the old notification silently
        tag: `arb-demo-${Date.now()}-${counter.current++}`
      })
    } catch (e) {
      // the constructor can throw outright, and without this the failure is invisible — which is the
      // one thing this page exists to prevent
      say(`Chrome refused to build the notification: ${e && e.message ? e.message : e}`)
      return
    }

    notification.onshow = () => {
      reported = true
      say('Chrome reports it as shown. Nothing on screen from here means macOS is holding it back.')
    }
    /**
     * The test is only a faithful test if it behaves like the real thing, and the real one is clickable.
     * There is no arb behind this one, so it goes to the feed.
     */
    notification.onclick = () => {
      window.focus()
      notification.close()
      say('Clicked — this is where a real one would open the arb.')
      navigate('/notifications')
    }
    notification.onerror = () => {
      reported = true
      say('Chrome failed to show it.')
    }

    setTimeout(() => {
      if (!reported) {
        say('Chrome never reported it as shown — the call was accepted and swallowed.')
      }
    }, 3000)
  }

  const sendNow = async () => {
    if (await ensurePermission()) {
      say('Sending now.')
      fire()
    }
  }

  const schedule = async () => {
    if (!(await ensurePermission())) {
      return
    }
    clearTimeout(timer.current)
    clearInterval(tick.current)
    setSecondsLeft(DELAY_SECONDS)
    say(`Scheduled for ${DELAY_SECONDS} seconds — switch to another tab.`)

    tick.current = setInterval(() => {
      setSecondsLeft((left) => (left === null || left <= 1 ? null : left - 1))
    }, 1000)

    timer.current = setTimeout(() => {
      clearInterval(tick.current)
      setSecondsLeft(null)
      fire()
    }, DELAY_SECONDS * 1000)
  }

  const state = {
    granted: 'Allowed. A notification appears even when this tab is in the background.',
    denied: 'Blocked. Chrome will not ask again — allow it in the padlock menu next to the address bar.',
    default: 'Not asked yet. The buttons below ask, because Chrome only accepts the request from a click.',
    unsupported: 'This browser has no Notification API.'
  }[permission]

  const buttonSx = {
    height: '38px',
    padding: '0 20px',
    fontSize: '14px',
    letterSpacing: '0.05em',
    color: '#ffffff',
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '2px',
    '&:hover': { backgroundColor: '#A81819' }
  }

  const ordered = [...mutes].sort((a, b) =>
    (SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope]) || a.label.localeCompare(b.label))

  return (
    <Box sx={{ padding: '24px' }}>
      <Box sx={{ backgroundColor: 'white', padding: '24px', maxWidth: '640px' }}>
        <Typography variant="h4" sx={{ marginBottom: '16px', color: theme.palette.primary.main }}>
          Notification settings
        </Typography>

        <FormControlLabel
          control={<Switch checked={settings.notificationSound !== false} onChange={toggleSound} color="secondary" />}
          label="Play a sound when a new arb arrives"
          sx={{ display: 'flex', '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
        />
        <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', marginBottom: '20px' }}>
          The sound plays from a background tab too and needs no permission from anybody.
        </Typography>

        <Typography variant="h4" sx={{ marginBottom: '6px', color: theme.palette.primary.main }}>
          Desktop notifications
        </Typography>
        <FormControlLabel
          control={<Switch checked={settings.desktopNotifications !== false} onChange={toggleDesktop} color="secondary" />}
          label="Show a desktop notification when a new arb arrives"
          sx={{ display: 'flex', '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
        />
        <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#6E7378', margin: '6px 0 14px' }}>
          {settings.desktopNotifications === false
            ? 'Off for this account. The sound above is unaffected — the two are separate decisions.'
            : state}
        </Typography>

        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button onClick={sendNow} sx={buttonSx}>Send one now</Button>
          <Button
            onClick={schedule}
            sx={{ ...buttonSx, backgroundColor: theme.palette.primary.light, '&:hover': { backgroundColor: '#069A72' } }}
          >
            {secondsLeft === null ? `Send in ${DELAY_SECONDS} seconds` : `Arriving in ${secondsLeft}s`}
          </Button>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: '12.5px', color: '#6E7378', marginTop: '12px' }}>
          The delayed one is for switching to another tab first — that is the case worth seeing.
        </Typography>

        {lines.length > 0 && (
          <Box sx={{ marginTop: '14px', padding: '10px 12px', backgroundColor: '#F6F6F8', border: '1px solid #E4E4E8' }}>
            {lines.map((line, index) => (
              <Typography
                key={index}
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '12px',
                  color: index === 0 ? theme.palette.primary.main : '#9A9EA3'
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}

        <Typography variant="h4" sx={{ margin: '28px 0 6px', color: theme.palette.primary.main }}>
          Telegram
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#6E7378', marginBottom: '12px' }}>
          Enter the ID of the Telegram chat you want to receive notifications in. You need to be in that
          chat, and so does the bot.
        </Typography>
        <TextField
          size="small"
          value={chatId}
          onChange={(event) => setChatId(event.target.value)}
          onBlur={saveChatId}
          placeholder="Chat ID"
          sx={{
            backgroundColor: 'white',
            borderRadius: '2px',
            width: '260px',
            '& .MuiOutlinedInput-input': { padding: '10px 12px', fontSize: '15px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C9C9D1' }
          }}
        />
        <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', marginTop: '6px' }}>
          Saved when you leave the field.
        </Typography>

        <Typography variant="h4" sx={{ margin: '28px 0 6px', color: theme.palette.primary.main }}>
          Muted
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', marginBottom: '10px' }}>
          Nothing listed here raises a notification. A muted game drops off this list once it has kicked
          off. Unmuting takes effect at once.
        </Typography>

        <Box sx={{ border: '1px solid #E4E4E8' }}>
          {ordered.length === 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#9A9EA3', padding: '12px' }}>
              Nothing is muted.
            </Typography>
          )}
          {ordered.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '7px 12px', borderBottom: '1px solid #F0F0F3'
              }}
            >
              <Box component="span" sx={{
                fontFamily: FONT, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.5px',
                textTransform: 'uppercase', color: '#9A9EA3', minWidth: '54px'
              }}>
                {SCOPE_LABEL[row.scope] || row.scope}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: '13.5px' }}>{row.label}</Typography>
                {/* two meetings of the same pair are otherwise the same line */}
                {row.koTime && (
                  <Typography sx={{ fontFamily: FONT, fontSize: '11.5px', color: '#9A9EA3' }}>
                    {clock.stamp(row.koTime)}
                  </Typography>
                )}
              </Box>
              <Box
                component="button"
                onClick={() => unmute(row.id)}
                sx={{
                  border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
                  padding: '3px 10px', fontFamily: FONT, fontSize: '12px', color: '#6E7378', cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.secondary.main, color: theme.palette.secondary.main }
                }}
              >
                Unmute
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default NotificationSettingsPage
